import type { ReactElement } from 'react'
import { chartCss } from '~/charts/theme'
import { Card, CardHead } from '~/components/ui/Card'
import type { ChipTone } from '~/components/ui/Status'
import { StatusPill } from '~/components/ui/Status'
import { DataTable } from '~/components/ui/Table'
import type { HistoryStanding } from '~/state/collections'
import { useCollectionStanding } from '~/state/collections'
import type { ImportRecord } from '~/state/sources'
import { useImportHistory } from '~/state/sources'

const PAGE_CSS = `
.nw-backfill-list { list-style: none; margin: 0; padding: 0; }
.nw-backfill-list li {
  align-items: center; border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule);
  display: flex; gap: var(--nw-space-3); padding: 10px 0;
}
.nw-backfill-list li:last-child { border-bottom: 0; }
.nw-backfill-list li > span { display: flex; flex-direction: column; gap: 2px; }
.nw-backfill-list small { color: var(--nw-muted); font-size: var(--nw-text-chip); }
.nw-imports .nw-data small { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); margin-top: 2px; }
`

const COLUMNS = [
  { id: 'source', label: 'Source', width: '170px' },
  { id: 'status', label: 'Status', width: '190px' },
  { id: 'date', label: 'Valued as of', width: '150px' },
  { id: 'produced', label: 'Produced' },
]

/**
 * Matched to the pills `Health` already draws for the same three verdicts, so
 * a source does not read as reporting on one tab and carrying forward on the
 * other. A tone is fill, edge and a glyph — never a colour alone.
 */
const TONE_OF: Record<ImportRecord['status'], ChipTone> = {
  ok: 'live',
  carried_forward: 'caution',
  error: 'failed',
}

const LABEL_OF: Record<ImportRecord['status'], string> = {
  ok: 'Reported',
  carried_forward: 'Carried forward',
  error: 'Failed',
}

/**
 * Where every figure nobody typed in came from: what a connected source read,
 * on which run, and what came of it — completed and, since a reconstruction
 * can run for days, still in progress too.
 */
export function ImportHistory(): ReactElement {
  const { data, error, isPending } = useImportHistory()
  const standing = useCollectionStanding()
  const filling = standing.data?.history ?? []

  return (
    <main className="nw nw-imports">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Import history</h1>
      <p className="nw-sub">
        What each connected source has read, when, and what it produced — the answer to "where did
        this figure come from" for a holding nobody typed in.
      </p>

      {filling.length > 0 && <Backfills filling={filling} />}

      {isPending && <p className="nw-empty">Loading…</p>}
      {error && (
        <p className="nw-flag nw-flag--missing">
          <span className="nw-flag-icon" aria-hidden="true">
            ✕
          </span>
          <span>Could not load the import history: {error.message}</span>
        </p>
      )}

      {data && (
        <Card>
          <CardHead
            title="Completed imports"
            lede="Every run a connected source has answered, most recent first. A source nothing here has connected keeps no row: every run writes one for it regardless, and this is not that bookkeeping."
          />
          {data.length === 0 ? (
            <p className="nw-empty">Nothing has run yet.</p>
          ) : (
            <DataTable columns={COLUMNS} label="Every import a source has made">
              {data.map((record) => (
                <Row key={`${record.runId}-${record.source}`} record={record} />
              ))}
            </DataTable>
          )}
        </Card>
      )}
    </main>
  )
}

/**
 * Legible on its own tab rather than folded into the table below: a
 * reconstruction is not a run that finished, so a row that looked like the
 * others would say it was one.
 */
function Backfills({ filling }: { filling: readonly HistoryStanding[] }): ReactElement {
  return (
    <Card>
      <CardHead
        title="Filling in the past"
        lede="A reconstruction can take days. This is how far each has actually got, not a promise about how far it will go."
      />
      <ul className="nw-backfill-list">
        {filling.map((one) => (
          <li key={one.source}>
            <StatusPill tone="caution" label="Filling in" glyph />
            <span>
              <b>{one.label}</b>
              <small>
                {one.reached === undefined
                  ? `${dayCount(one.written)} written so far.`
                  : `Reached ${one.reached} — ${dayCount(one.written)} written so far.`}
              </small>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function Row({ record }: { record: ImportRecord }): ReactElement {
  return (
    <tr>
      <th scope="row">{record.label}</th>
      <td>
        <StatusPill tone={TONE_OF[record.status]} label={LABEL_OF[record.status]} glyph />
        {record.error !== undefined && <small>{record.error}</small>}
        {record.staleSince !== undefined && <small>carried forward since {record.staleSince}</small>}
      </td>
      <td>{record.asOf}</td>
      <td>
        {record.accountsReported === 0
          ? 'nothing'
          : `${record.accountsReported} account${record.accountsReported === 1 ? '' : 's'}`}
      </td>
    </tr>
  )
}

function dayCount(days: number): string {
  return `${days} day${days === 1 ? '' : 's'}`
}
